/**
 * parseDebaucheFile.js — Lecture d'un fichier Excel/CSV de débauche en masse.
 *
 * Le fichier attendu est volontairement simple : une colonne « Matricule »
 * (obligatoire) et, en option, des colonnes « Nom » / « Motif » à titre
 * informatif. La résolution réelle (existence, type, foyer) est faite côté
 * backend à partir des matricules.
 */

const HEADER_ALIASES = {
  // « Mlle » est l'intitulé de la colonne matricule dans le modèle DNS officiel.
  matricule: [
    'MATRICULE',
    'MATRICULES',
    'MAT',
    'MLLE',
    'MLE',
    'N°',
    'Nº',
    'NO',
    'NUMERO',
    'NUMÉRO',
    'MATRICULE AGENT',
    'N° MATRICULE',
    'Nº MATRICULE',
    'NO MATRICULE',
    'NUMÉRO MATRICULE',
    'NUMERO MATRICULE'
  ],
  nom: ['NOM', 'NOMS'],
  prenom: ['PRENOM', 'PRENOMS', 'PRÉNOM', 'PRÉNOMS'],
  motif: ['MOTIF', 'RAISON', 'MOTIF DEBAUCHE', 'MOTIF DE DEBAUCHE', 'MOTIF DÉBAUCHE']
};

const norm = (v) => String(v == null ? '' : v).trim().toUpperCase();

function findColumns(headerRow) {
  const cols = { matricule: -1, nom: -1, prenom: -1, motif: -1 };
  headerRow.forEach((cell, idx) => {
    const value = norm(cell);
    if (!value) return;
    Object.keys(HEADER_ALIASES).forEach((field) => {
      if (cols[field] === -1 && HEADER_ALIASES[field].includes(value)) {
        cols[field] = idx;
      }
    });
  });
  return cols;
}

function isDnsHeaderRow(row = []) {
  return row[0] === 'Nb' || String(row[1] || '').toUpperCase().includes('LIEU');
}

/**
 * @param {Array<Array<any>>} data - issu de XLSX.utils.sheet_to_json(sheet, { header: 1 }).
 * @returns {{ rows: Array<{matricule:string, nom:string, motif:string, rowNumber:number}>, skipped:number }}
 * @throws {Error} si aucune colonne « Matricule » n'est trouvée.
 */
export function parseDebaucheFile(data) {
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('Fichier vide ou illisible.');
  }

  // Recherche de la ligne d'en-tête contenant « Matricule » (parmi les 15 premières).
  // Fallback : le fichier DNS officiel n'a pas toujours un libellé "Matricule"
  // explicite, mais place le matricule en colonne 3, nom en colonne 4, prénom en 5.
  // Si aucun matricule n'est fourni mais que le fichier contient Nom/Prénom,
  // on garde les lignes pour une résolution backend par identité.
  let headerIndex = -1;
  let cols = null;
  const scanLimit = Math.min(data.length, 15);
  for (let i = 0; i < scanLimit; i++) {
    const row = data[i] || [];
    const candidate = findColumns(row);
    if (candidate.matricule !== -1) {
      headerIndex = i;
      cols = candidate;
      break;
    }
    if (candidate.nom !== -1) {
      headerIndex = i;
      cols = candidate;
      break;
    }
    if (isDnsHeaderRow(row)) {
      headerIndex = i;
      cols = { matricule: 2, nom: 3, prenom: 4, motif: -1 };
      break;
    }
  }

  if (headerIndex === -1 || !cols) {
    throw new Error(
      'Colonne des matricules introuvable. Utilisez un en-tête « Matricule » ' +
        '(ou « Mlle » pour un fichier DNS), ou téléchargez le modèle.'
    );
  }

  const rows = [];
  let skipped = 0;

  for (let i = headerIndex + 1; i < data.length; i++) {
    const row = data[i] || [];
    if (String(row[1] || '').includes('EFFECTIF')) break;

    const matricule = cols.matricule >= 0 ? String(row[cols.matricule] || '').trim() : '';
    const nom = cols.nom >= 0 ? String(row[cols.nom] || '').trim() : '';
    const prenom = cols.prenom >= 0 ? String(row[cols.prenom] || '').trim() : '';

    if (!matricule && !nom) {
      skipped += 1;
      continue;
    }

    rows.push({
      matricule,
      nom,
      prenom,
      motif: cols.motif >= 0 ? String(row[cols.motif] || '').trim() : '',
      rowNumber: i + 1
    });
  }

  if (rows.length === 0) {
    throw new Error('Aucun adhérent exploitable trouvé dans le fichier.');
  }

  return { rows, skipped };
}

export default parseDebaucheFile;
